import React, { useEffect, useMemo, useState, useRef } from "react";
import css from "./style.scss";
import uparrow from "../../assets/images/uparrow.svg";
import downarrow from "../../assets/images/downarrow.svg";

/**
 * Campaign combobox for Campaign Activity Settings modal: `onSearchChange`, Enter to commit,
 * "Create …" when no exact list match, optional layout class names. Elsewhere use CampaignAutoDropdown.
 */
const CampaignModalAutoDropdown = ({
    items,
    label,
    placeholder,
    onSelect,
    onSearchChange,
    disabled = false,
    required = false,
    value = null,
    noDataMessage = null,
    showLabel = true,
    inputWrapperClassName = "",
    inputClassName = "",
    arrowButtonClassName = "",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(value);
    const [searchValue, setSearchValue] = useState("");
    const containerRef = useRef(null);
    const clearSearchValue = () => {
        setSearchValue("");
        if (onSearchChange) onSearchChange("");
    };

    useEffect(() => {
        if (value && value.name) {
            setSelectedItem(value);
        } else {
            setSelectedItem(null);
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                clearSearchValue();
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const primeSearchWithSelected = () => {
        const initial = selectedItem?.name || "";
        setSearchValue(initial);
        if (onSearchChange) onSearchChange(initial);
    };

    const openDropdown = () => {
        primeSearchWithSelected();
        setIsOpen(true);
    };

    const toggleDropdown = () => {
        setIsOpen((prev) => {
            if (!prev) {
                primeSearchWithSelected();
            } else {
                clearSearchValue();
            }
            return !prev;
        });
    };

    const filteredItems = useMemo(() => {
        const term = searchValue.trim().toLowerCase();
        if (!term) return items;
        return items.filter((item) => item.name.toLowerCase().includes(term));
    }, [searchValue, items]);

    const showCreateNewOption = useMemo(() => {
        const trimmed = searchValue.trim();
        if (!trimmed) return false;
        return !items.some(
            (i) =>
                !i.isGroup &&
                i.name.toLowerCase() === trimmed.toLowerCase(),
        );
    }, [searchValue, items]);

    const inputDisplayValue = isOpen ? searchValue : (selectedItem?.name || "");
    const typedValue = searchValue.trim().toLowerCase();
    const visibleItemsCount = filteredItems.length + (showCreateNewOption ? 1 : 0);

    return (
        <div className={`${css.autocompleteContainer} campaign-auto-dropdown`} ref={containerRef}>
            {showLabel && (
                <label className={css.campaignLabel}>
                    {label} {required && <span className={css.requiredAsterisk}> *</span>}
                </label>
            )}

            <div
                className={[
                    css.autocompleteInputWrapper,
                    isOpen ? css.open : "",
                    inputWrapperClassName,
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                <input
                    type="text"
                    value={inputDisplayValue}
                    onChange={(e) => {
                        const v = e.target.value;
                        setSearchValue(v);
                        setIsOpen(true);
                        if (onSearchChange) onSearchChange(v);
                    }}
                    onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        const trimmed = searchValue.trim();
                        if (!trimmed) return;

                        const exactMatch = items.find(
                            (i) => !i.isGroup && i.name.toLowerCase() === trimmed.toLowerCase(),
                        );
                        const chosen = exactMatch || { name: trimmed, id: undefined };

                        setSelectedItem(chosen);
                        setIsOpen(false);
                        setSearchValue("");
                        if (onSearchChange) onSearchChange(chosen.name || "");
                        if (onSelect) onSelect(chosen);
                    }}
                    onFocus={openDropdown}
                    placeholder={placeholder}
                    className={[css.campaginAutocompleteInput, inputClassName]
                        .filter(Boolean)
                        .join(" ")}
                    disabled={disabled}
                />

                <button
                    className={[css.dropdownArrow, arrowButtonClassName]
                        .filter(Boolean)
                        .join(" ")}
                    onClick={toggleDropdown}
                    type="button"
                    disabled={disabled}
                >
                    <img
                        src={isOpen ? uparrow : downarrow}
                        alt="arrow"
                        className={css.arrowIcon}
                    />
                </button>

            </div>

            {isOpen && (
                <ul
                    className={[
                        css.autocompleteList,
                        visibleItemsCount === 2 ? css.twoItems : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                    {showCreateNewOption && (
                        <li
                            key="__create__"
                            className={css.campaginAutoItem}
                            onMouseDown={() => {
                                const trimmed = searchValue.trim();
                                const chosen = { name: trimmed, id: undefined };
                                setSelectedItem(chosen);
                                setIsOpen(false);
                                setSearchValue("");
                                if (onSearchChange) onSearchChange(trimmed);
                                if (onSelect) onSelect(chosen);
                            }}
                        >
                            <span className="campaign-item-text">
                                Create &quot;{searchValue.trim()}&quot;
                            </span>
                        </li>
                    )}
                    {filteredItems.map((item) =>
                        item.isGroup ? (
                            <li key={item.id} className={css.groupHeading}>
                                {item.name}
                            </li>
                        ) : (
                            <li
                                key={item.id}
                                className={[
                                    css.campaginAutoItem,
                                    typedValue && item.name.toLowerCase() === typedValue
                                        ? css.exactMatchHover
                                        : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                onMouseDown={() => {
                                    setSelectedItem(item);
                                    setIsOpen(false);
                                    setSearchValue("");

                                    if (onSearchChange) onSearchChange(item.name);
                                    if (onSelect) onSelect(item);
                                }}
                            >
                                <span className="campaign-item-text">{item.name}</span>
                            </li>
                        )
                    )}
                </ul>
            )}
        </div>
    );
};

export default CampaignModalAutoDropdown;
